import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, FileText, Users, Receipt, MessageCircle, Wallet, FolderOpen, Hash, Shield, Zap, Bot, Check } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";

const FEATURES = [
  { icon: Users, title: "Client Master", body: "TaxPro-style client database with group/sub-group, constitution, GSTIN, PAN, return type, contact persons, and portal logins. Bulk import 1000+ clients via Excel/CSV in seconds." },
  { icon: FileText, title: "Tax Return Tracker", body: "Track every GST · ITR · TDS · ROC return across all your clients. Due-date alerts, status workflow, period-wise filing log." },
  { icon: Receipt, title: "Vyapar-style GST Invoicing", body: "Multi-line invoices with CGST/SGST/IGST, place of supply, HSN/SAC, professional A4 PDF generation, partial payments and outstanding tracking." },
  { icon: Wallet, title: "Accounting & Books", body: "Bank accounts, expenses by category, cash summary, firm settings, and a full accounting trail. Vyapar-grade reporting." },
  { icon: Bot, title: "AI Assistant (GPT-5.2)", body: "AI-drafted WhatsApp messages, return-filing checklists, client insights, payment chasers, HSN/SAC finder — your virtual CA junior." },
  { icon: MessageCircle, title: "WhatsApp Connect", body: "One-click chat-with-client, share invoices, send AI-drafted reminders, and keep a full conversation log per client." },
  { icon: FolderOpen, title: "Document Vault", body: "Per-client document storage up to 50 MB each. KYC, GST workings, bank statements, agreements — all in one secure place." },
  { icon: Hash, title: "HSN / SAC Finder", body: "AI-powered HSN/SAC code lookup with the correct GST rate. No more digging through PDFs." },
  { icon: Shield, title: "Granular Team Access", body: "Per-user permissions (clients_view, invoices_write, returns_filed, etc.). Articled clerks see only what you let them." },
];

const STATS = [
  { value: "10+", label: "Modules in one OS" },
  { value: "GPT-5.2", label: "AI engine inside" },
  { value: "7-day", label: "Free trial" },
  { value: "₹999/mo", label: "After trial" },
];

const FAQS = [
  { q: "Who is TAXINPHO for?", a: "Indian Chartered Accountants and CA firms managing GST, Income Tax, TDS, ROC compliance for their clients. Works equally well for solo practitioners and 50-staff firms." },
  { q: "How long is the free trial?", a: "7 days of full feature access. No credit card required. Cancel anytime — your data stays safe even if you don't subscribe." },
  { q: "What does it cost after the trial?", a: "₹999/month or ₹9,999/year (save ~17% on annual). All features included. No hidden fees or per-user charges." },
  { q: "Is my client data safe?", a: "Yes. All data is encrypted in transit (TLS 1.3) and at rest. We never share your data with third parties. You can export or delete your data anytime." },
  { q: "Can my team use it together?", a: "Yes — invite unlimited team members under your account. Use granular permissions to control exactly who can see/edit clients, invoices, returns, and reports." },
  { q: "Does it support GST filing directly?", a: "TAXINPHO tracks return-filing status, generates working sheets, and integrates with GST/IT portal logins via secure clipboard copy + tab launch. Actual filing happens on the official government portals, as required by law." },
];

export default function Landing() {
  return (
    <MarketingShell>
      <SEO
        title="TAXINPHO — All-in-one OS for Indian CA offices"
        description="Manage clients, GST/ITR/TDS returns, invoices, accounting, AI assistant and WhatsApp — all in one workspace built for Indian Chartered Accountants. Start your 7-day free trial."
        canonical="https://taxinpho.co/"
      />
      {/* Hero */}
      <section className="relative bg-zinc-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40"
             style={{ backgroundImage: "radial-gradient(circle at 15% 25%, #002FA7 0%, transparent 45%), radial-gradient(circle at 85% 75%, #1ABEDC 0%, transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-1.5 bg-[#1ABEDC] text-zinc-950 px-2.5 py-1 text-xs font-bold mb-5">
              <Sparkles size={12} /> POWERED BY GPT-5.2
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92] mb-6">
              The OS for<br />Indian CA offices.
            </h1>
            <p className="text-lg text-zinc-300 max-w-2xl leading-relaxed mb-8">
              Stop juggling 7 tools. TAXINPHO is your single workspace for clients,
              GST/ITR returns, invoices, accounting, AI drafts, and team collaboration —
              built by a CA, for CAs.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="bg-white text-zinc-950 px-6 py-3 font-semibold text-base hover:bg-zinc-200 flex items-center gap-2 group"
              >
                Start 7-day free trial
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-cta-secondary"
                className="border border-zinc-700 text-white px-6 py-3 font-semibold text-base hover:bg-zinc-800 flex items-center gap-2"
              >
                See pricing
              </Link>
            </div>
            <p className="text-xs text-zinc-500 mt-5">No card required · Cancel anytime · ₹999/mo or ₹9,999/yr after trial</p>
          </div>

          {/* Hero illustration / metric panel */}
          <div className="lg:col-span-5">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 relative">
              <div className="flex items-center justify-between mb-5">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">Today · Adv Mrinal</p>
                <span className="text-[#1ABEDC] text-xs font-mono">[LIVE]</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-black/30 p-3 border border-white/5">
                  <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center font-bold text-white">42</div>
                  <div>
                    <div className="text-xs text-zinc-400">Active clients</div>
                    <div className="text-sm font-semibold">Across GST · ITR · TDS</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-black/30 p-3 border border-white/5">
                  <div className="w-9 h-9 bg-[#F79009] flex items-center justify-center font-bold text-white">7</div>
                  <div>
                    <div className="text-xs text-zinc-400">Returns due this week</div>
                    <div className="text-sm font-semibold">GSTR-1 · GSTR-3B · TDS-Q2</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-black/30 p-3 border border-white/5">
                  <div className="w-9 h-9 bg-[#039855] flex items-center justify-center font-bold text-white text-xs">₹</div>
                  <div>
                    <div className="text-xs text-zinc-400">Collected this month</div>
                    <div className="text-sm font-semibold">₹2,84,500</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#1ABEDC]/10 p-3 border border-[#1ABEDC]/30">
                  <div className="w-9 h-9 bg-[#1ABEDC] flex items-center justify-center"><Sparkles size={16} className="text-zinc-950" /></div>
                  <div>
                    <div className="text-xs text-[#1ABEDC]">AI suggestion</div>
                    <div className="text-sm font-semibold">Chase Rajesh Traders — ₹45k overdue 12d</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 py-7 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center sm:text-left">
                <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why TAXINPHO */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3">Why TAXINPHO</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
            One workspace.<br />Every compliance task.
          </h2>
          <p className="text-zinc-600 text-lg leading-relaxed">
            Stop switching between Excel sheets, WhatsApp, email, Tally, GST portal,
            ITR utility and 5 other tools. Run your entire practice from a single
            screen — and let AI handle the boring parts.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-zinc-200 p-6 hover:border-zinc-900 transition-colors">
              <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center mb-4">
                <f.icon size={18} />
              </div>
              <h3 className="font-display font-bold text-xl tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison strip */}
      <section className="bg-zinc-950 text-white py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-3xl mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#1ABEDC] mb-3">Before vs After</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
              What CAs say about us.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-zinc-900 border border-zinc-800 p-7">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Before TAXINPHO</p>
              <ul className="space-y-2.5 text-sm text-zinc-400">
                <li>· 8 tabs open all day — Excel, GST portal, WhatsApp Web, ITR utility, Gmail, Tally...</li>
                <li>· Manually copy GSTIN, PAN, password from a notepad every login</li>
                <li>· Lose track of which client&apos;s return is due when</li>
                <li>· Articles asking &quot;what&apos;s the HSN for this product?&quot; 30 times a day</li>
                <li>· Spend 2 hours writing payment reminder messages</li>
                <li>· No idea who&apos;s overdue or which work is profitable</li>
              </ul>
            </div>
            <div className="bg-[#1ABEDC]/10 border border-[#1ABEDC]/40 p-7">
              <p className="text-xs uppercase tracking-widest text-[#1ABEDC] mb-3">After TAXINPHO</p>
              <ul className="space-y-2.5 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />One screen for clients, returns, invoices, AI, accounting</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />One-click portal launch with auto-copied credentials</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />Due-date dashboard with AI-prioritised chase list</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />AI HSN finder built right inside the invoice form</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />GPT-5.2 drafts your WhatsApp reminders in your tone</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-1 flex-shrink-0" />Live revenue, expenses, outstanding, return-status charts</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3">Pricing</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
            Less than one client&apos;s fee.
          </h2>
          <p className="text-zinc-600 text-lg leading-relaxed">
            For the cost of one professional billing per month, run your entire CA practice.
            Cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          <div className="border-2 border-zinc-200 p-7">
            <p className="font-display font-bold text-xl mb-1">Free trial</p>
            <p className="font-display font-black text-5xl tracking-tighter mb-1">₹0</p>
            <p className="text-sm text-zinc-600 mb-5">For 7 days · all features</p>
            <Link to="/register" data-testid="pricing-trial-cta" className="block text-center bg-zinc-100 text-zinc-900 py-2.5 font-semibold hover:bg-zinc-200">
              Start trial
            </Link>
          </div>
          <div className="border-2 border-[#002FA7] bg-[#EFF4FF] p-7 relative">
            <span className="absolute -top-3 left-6 bg-[#002FA7] text-white text-xs font-bold px-3 py-1">MOST POPULAR</span>
            <p className="font-display font-bold text-xl mb-1">Monthly</p>
            <p className="font-display font-black text-5xl tracking-tighter mb-1">₹999<span className="text-xl text-zinc-500">/mo</span></p>
            <p className="text-sm text-zinc-600 mb-5">Cancel anytime</p>
            <Link to="/register" data-testid="pricing-monthly-cta" className="block text-center bg-[#002FA7] text-white py-2.5 font-semibold hover:bg-blue-800">
              Get started
            </Link>
          </div>
          <div className="border-2 border-zinc-900 p-7 bg-zinc-950 text-white">
            <p className="font-display font-bold text-xl mb-1">Yearly</p>
            <p className="font-display font-black text-5xl tracking-tighter mb-1">₹9,999<span className="text-xl text-zinc-500">/yr</span></p>
            <p className="text-sm text-zinc-400 mb-5">Save ~17% vs monthly</p>
            <Link to="/register" data-testid="pricing-yearly-cta" className="block text-center bg-[#1ABEDC] text-zinc-950 py-2.5 font-semibold hover:bg-cyan-400">
              Save with yearly
            </Link>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/pricing" className="text-sm font-semibold underline underline-offset-4">
            See full pricing details →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-zinc-50 py-20">
        <div className="max-w-3xl mx-auto px-5">
          <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3 text-center">Questions</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-center mb-12">
            Frequently asked.
          </h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="border border-zinc-200 bg-white p-5 group">
                <summary className="cursor-pointer font-semibold text-zinc-900 flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="text-zinc-400 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="text-sm text-zinc-600 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-zinc-950 text-white py-20">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Zap size={32} className="text-[#1ABEDC] mx-auto mb-5" />
          <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mb-5">
            Run your CA office.<br />Like it&apos;s 2026.
          </h2>
          <p className="text-lg text-zinc-300 mb-8 max-w-xl mx-auto">
            Set up your office, import 100 clients, and file your first GST return through TAXINPHO — all in one afternoon.
          </p>
          <Link
            to="/register"
            data-testid="footer-cta"
            className="inline-flex items-center gap-2 bg-[#1ABEDC] text-zinc-950 px-7 py-3.5 font-semibold text-base hover:bg-cyan-400"
          >
            Start your free trial <ArrowRight size={16} />
          </Link>
          <p className="text-xs text-zinc-500 mt-5">No credit card · ₹999/month after trial · Made in India</p>
        </div>
      </section>
    </MarketingShell>
  );
}
